import { Button, Tooltip, useDisclosure } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  useCreateExpenseMutation,
  useUpdateExpenseMutation,
} from "@src/__generated__/graphql";
import { DocumentLimitTypeEnum } from "@src/__generated__/urql-graphql";
import { ModalConfirm } from "@src/components/ui-kit";
import { onError } from "@src/utils/apollo";
import { useStore as useAppStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { useStore } from "../../store";

type OnSaveProps = {
  onSave?: () => void;
  preventSaveDueToDuplicates: boolean;
};
export const Save = observer<OnSaveProps>(function Save({
  onSave,
  preventSaveDueToDuplicates,
}) {
  const store = useStore();
  const appStore = useAppStore();
  const is_draft = false;
  const for_approval = false;
  const [isValidating, setIsValidating] = useState(false);

  const expenseLimitStatus = appStore.authStore.getDocumentLimitStatus(
    DocumentLimitTypeEnum.Expense,
    store.convertedAmount ?? store.totalAmountForExpense,
  );

  const [create, { loading: createLoading }] = useCreateExpenseMutation({
    onCompleted() {
      appStore.UIStore.toast({
        title: t`The expense was created.`,
        status: "success",
      });
      onSave?.();
    },
    ...onError(),
  });

  const [update, { loading: updateLoading }] = useUpdateExpenseMutation({
    onCompleted() {
      appStore.UIStore.toast({
        title: t`The expense was updated.`,
        status: "success",
      });
      onSave?.();
    },
    ...onError(),
  });

  const confirmModal = useDisclosure();

  const validateAndSave = async () => {
    setIsValidating(true);
    if (store.contractor) {
      const { hasError: contractorHasError } =
        await store.editContractorStore.form.validateForExpense(
          store.contractorOptionalValidateFields,
        );
      if (contractorHasError) {
        setIsValidating(false);
        store.editContractorStore.toggleOpen(true);
        return;
      }
    }

    const { hasError } = await store.form.validate();
    setIsValidating(false);

    if (hasError) {
      return store.setScrollTo();
    }

    if (
      store.canUserAccess("two_percent_fund") &&
      store.form.two_percent_fund.value
    ) {
      confirmModal.onOpen();
    } else {
      save();
    }
  };

  const save = () => {
    if (store.expense?.id) {
      update({
        variables: { input: store.inputUpdate({ is_draft, for_approval }) },
      });
    } else {
      create({
        variables: { input: store.inputCreate({ is_draft, for_approval }) },
      });
    }
  };

  const isDisabledByLimit = !expenseLimitStatus.allowed;
  const isDisabled =
    !store.everyItemApproved ||
    store.isDocumentLocked ||
    store.expenseUpdatedWhileInactive ||
    isDisabledByLimit ||
    store.hasModelLock ||
    preventSaveDueToDuplicates ||
    store.vatTotalsMismatch;

  const getTooltipLabel = () => {
    if (preventSaveDueToDuplicates) {
      return t`Cannot save because this expense has duplicates.`;
    }
    if (isDisabledByLimit) {
      return expenseLimitStatus.tooltipLabel;
    }
    if (!store.everyItemApproved) {
      return t`Cannot save until all budget items are approved.`;
    }
    return undefined;
  };

  return (
    <React.Fragment>
      <Tooltip
        isDisabled={
          store.everyItemApproved &&
          !isDisabledByLimit &&
          !preventSaveDueToDuplicates
        }
        label={getTooltipLabel()}
      >
        <Button
          isDisabled={isDisabled}
          isLoading={createLoading || updateLoading || isValidating}
          onClick={validateAndSave}
        >
          <Trans>Save Approved</Trans>
        </Button>
      </Tooltip>
      <ModalConfirm {...confirmModal} onConfirm={save}>
        <Trans>
          In this Expense will be used 2 % payment, therefore after saving this
          Expense will be split into two Expenses where one will have 98 % and
          second 2% of Total amount and Budget split. You cannot undo this
          action. Are you sure you want to save this Expense?
        </Trans>
      </ModalConfirm>
    </React.Fragment>
  );
});
